import React, { Component } from 'react';
import { func, string, bool, shape, arrayOf, number } from 'prop-types';
import classnames from 'classnames';
import compose from 'lodash/flowRight';
import MediaQuery from 'react-responsive';
import { FormattedMessage } from 'react-intl';
import DisableScroll from 'utils/components/DisableScroll/DisableScroll';
import { facets as facetUtil } from 'utils';
import {
    withFiltersConfig,
    withListingTypesConfig,
    withListingType,
    withCategory,
} from 'connectors';
import { filtersConfigShape, listingTypesShape, categoryShape } from 'shapes';
import { MasslandingBlock, TopSeoContent } from 'modules';
import FilterCollapsePanel from './FilterCollapsePanel/FilterCollapsePanel';
import CriteriaWrapper from '../CriteriaWrapper/CriteriaWrapper';
import FacetFilter from './FacetFilter/FacetFilter';
import ShowResultsButton from './ShowResultsButton/ShowResultsButton';
import './Filters.scss';
import './Filters.old.scss';

class Filters extends Component {
    static propTypes = {
        className: string,
        theme: shape({
            items: string,
        }),
        open: bool,
        specificFilters: arrayOf(
            shape({
                label: string,
                type: string,
                order: number,
            }),
        ),
        hasCategoryDetected: bool,
        onClose: func.isRequired,
        mobileWidth: number,
        excludedFilters: arrayOf(string),
        nbResults: number,
        filtersConfig: filtersConfigShape.isRequired, // eslint-disable-line react/no-unused-prop-types
        listingTypes: listingTypesShape.isRequired, // eslint-disable-line react/no-unused-prop-types
        listingType: string,
        category: categoryShape,
    };

    static defaultProps = {
        className: null,
        theme: {},
        open: false,
        hasCategoryDetected: false,
        nbResults: 0,
        specificFilters: [],
        mobileWidth: 830,
        excludedFilters: [],
        listingType: null,
        category: {},
    };

    static getDerivedStateFromProps(props, state) {
        const { specificFilters, excludedFilters, filtersConfig } = props;
        if (props.specificFilters !== state.specificFilters) {
            return {
                filters: facetUtil
                    .getFacetFilters({
                        specificFilters,
                        excludedFilters,
                        filtersConfig,
                        mandatoryFilters: props.listingTypes.mandatoryFilters,
                    })
                    .sort((filterA, filterB) => filterA.order - filterB.order),
            };
        }
        return null;
    }

    // eslint-disable-next-line react/state-in-constructor
    state = {
        filters: [],
    };

    shouldComponentUpdate(nextProps, nextState) {
        const {
            className,
            hasCategoryDetected,
            open,
            nbResults,
            specificFilters,
            theme,
        } = this.props;
        const { filters } = this.state;

        return (
            className !== nextProps.className ||
            open !== nextProps.open ||
            theme !== nextProps.theme ||
            hasCategoryDetected !== nextProps.hasCategoryDetected ||
            specificFilters !== nextProps.specificFilters ||
            filters !== nextState.filters ||
            nbResults !== nextProps.nbResults
        );
    }

    handleMobileClose = evt => {
        this.props.onClose(evt);
    };

    render() {
        const {
            className,
            theme = {},
            open,
            mobileWidth,
            nbResults,
            excludedFilters,
            specificFilters,
            listingType,
            category,
        } = this.props;
        const { filters } = this.state;
        const contentClassnames = classnames('filters__content', {
            'filters__content--open': open,
        });
        const rootClassnames = classnames(
            'filters',
            {
                'filters--open': open,
            },
            className,
        );
        const itemsClassnames = classnames('filters__items', theme.items);
        const categoryFiltersTheme = {
            children: 'filters__content__category-filters',
        };

        const shouldShowCategoryFilters =
            listingType === 'category' &&
            !!(
                /* eslint-disable camelcase */
                (
                    category.context?.sibling_categories?.length ||
                    category.context?.child_categories?.length
                )
                /* eslint-enable camelcase */
            );

        return (
            <div ref={this.handleRootRef} className={rootClassnames}>
                {category &&
                    category.context &&
                    category.context.seo_blocks &&
                    category.context.seo_blocks.top_description && (
                        <TopSeoContent
                            values={category.context.seo_blocks.top_description}
                        />
                    )}
                {open && (
                    <MediaQuery maxWidth={mobileWidth}>
                        <DisableScroll allowScrollOn=".filters__content" />
                    </MediaQuery>
                )}
                <div className={contentClassnames}>
                    {/* This category filter need to be exported in new one */}
                    {shouldShowCategoryFilters && (
                        <div className="filter--xs-slide-up">
                            <h2 className="filter__title">
                                <FormattedMessage
                                    id="listing.filters.categories"
                                    defaultMessage="Categories"
                                    description="Title of the switchable category list"
                                />
                            </h2>
                            <FilterCollapsePanel
                                className="filter__title"
                                theme={categoryFiltersTheme}
                                openedByDefault
                                clickToggleDisabled
                            >
                                <FacetFilter
                                    className="filters__items__item"
                                    zIndex={filters.length}
                                    facet={{ type: 'categoryList' }}
                                    openedByDefault
                                    mobileWidth={mobileWidth}
                                />
                            </FilterCollapsePanel>
                        </div>
                    )}
                    <CriteriaWrapper
                        className="filters__criteria"
                        excludedFilters={excludedFilters}
                        specificFilters={specificFilters}
                    />
                    <h2 className="filter__title filter__title--desktop">
                        <FormattedMessage
                            id="listing.filters.title"
                            defaultMessage="Filters"
                            description="Title of the filters panel"
                        />
                    </h2>
                    <div className={itemsClassnames}>
                        {filters.map((facet, index) => (
                            <FacetFilter
                                key={facet.attributeName}
                                className="filters__items__item"
                                zIndex={filters.length - index}
                                facet={facet}
                                openedByDefault={index <= 3}
                                mobileWidth={mobileWidth}
                            />
                        ))}
                    </div>
                </div>
                <ShowResultsButton
                    nbHits={nbResults}
                    onClick={this.handleMobileClose}
                    className="filter__show_results_button"
                />
                <MasslandingBlock className="filter__masslanding-block" />
            </div>
        );
    }
}

export default compose(
    withFiltersConfig,
    withListingTypesConfig,
    withListingType,
    withCategory,
)(Filters);
